import { z } from 'zod';

const McpOAuthCallbackMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('MCP_OAUTH_SUCCESS'),
    code: z.string().optional(),
    state: z.string().optional(),
  }),
  z.object({
    type: z.literal('MCP_OAUTH_ERROR'),
    error: z
      .unknown()
      .transform((value) =>
        typeof value === 'string' ? value : 'Authorization failed',
      ),
    state: z.string().optional(),
  }),
]);

export type McpOAuthCallbackMessage = z.infer<
  typeof McpOAuthCallbackMessageSchema
>;

export function parseMcpOAuthCallbackMessage(
  data: unknown,
): McpOAuthCallbackMessage | null {
  const parsed = McpOAuthCallbackMessageSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
}

function mcpOAuthMessageUsesOpenerPostMessageOnly(
  message: McpOAuthCallbackMessage,
): boolean {
  return message.type === 'MCP_OAUTH_SUCCESS' && Boolean(message.code);
}

export function buildMcpOAuthTerminalSuccessMessage({
  state,
}: {
  state?: string | null;
} = {}): Extract<McpOAuthCallbackMessage, { type: 'MCP_OAUTH_SUCCESS' }> {
  return state
    ? { type: 'MCP_OAUTH_SUCCESS', state }
    : { type: 'MCP_OAUTH_SUCCESS' };
}

export function buildMcpOAuthTerminalErrorMessage({
  error,
  state,
}: {
  error: string;
  state?: string | null;
}): Extract<McpOAuthCallbackMessage, { type: 'MCP_OAUTH_ERROR' }> {
  return state
    ? { type: 'MCP_OAUTH_ERROR', error, state }
    : { type: 'MCP_OAUTH_ERROR', error };
}

type PublishMcpOAuthResultInput = {
  message: McpOAuthCallbackMessage;
  opener?: {
    postMessage: (
      message: McpOAuthCallbackMessage,
      targetOrigin: string,
    ) => void;
  } | null;
  origin: string;
  broadcastChannel: {
    new (name: string): {
      postMessage: (message: McpOAuthCallbackMessage) => void;
      close: () => void;
    };
  };
};

export type McpOAuthGatewayExchangeResult =
  | { success: true }
  | { success: false; error: string };

export function buildMcpOAuthGatewayCallbackUrl({
  gatewayUrl,
  code,
  state,
}: {
  gatewayUrl: string;
  code: string;
  state: string;
}): string {
  const callbackUrl = new URL(
    `${gatewayUrl.replace(/\/$/, '')}/ai/mcp-servers/oauth/callback`,
  );
  callbackUrl.searchParams.set('code', code);
  callbackUrl.searchParams.set('state', state);
  return callbackUrl.toString();
}

export function parseMcpOAuthGatewayExchangeResponse(
  response: Pick<Response, 'status' | 'headers' | 'url' | 'ok'>,
): McpOAuthGatewayExchangeResult {
  if (response.status >= 400) {
    return {
      success: false,
      error: 'Failed to complete MCP OAuth authorization',
    };
  }

  if (response.status >= 300 && response.status < 400) {
    const location = response.headers.get('location');
    if (!location) {
      return {
        success: false,
        error: 'Failed to complete MCP OAuth authorization',
      };
    }

    try {
      const redirectUrl = new URL(location, response.url);
      const result = redirectUrl.searchParams.get('mcp_oauth_result');
      if (result === 'success') {
        return { success: true };
      }
      if (result === 'error') {
        return {
          success: false,
          error:
            redirectUrl.searchParams.get('error') ??
            'Failed to complete MCP OAuth authorization',
        };
      }
    } catch {
      return {
        success: false,
        error: 'Failed to complete MCP OAuth authorization',
      };
    }

    return {
      success: false,
      error: 'Failed to complete MCP OAuth authorization',
    };
  }

  if (response.ok) {
    return { success: true };
  }

  return {
    success: false,
    error: 'Failed to complete MCP OAuth authorization',
  };
}

export async function exchangeMcpOAuthCallbackOnGateway({
  gatewayUrl,
  code,
  state,
}: {
  gatewayUrl: string;
  code: string;
  state: string;
}): Promise<McpOAuthGatewayExchangeResult> {
  const response = await fetch(
    buildMcpOAuthGatewayCallbackUrl({ gatewayUrl, code, state }),
    {
      method: 'GET',
      redirect: 'manual',
    },
  );

  return parseMcpOAuthGatewayExchangeResponse(response);
}

export async function completeMcpOAuthCallback({
  gatewayUrl,
  code,
  state,
}: {
  gatewayUrl: string;
  code: string;
  state: string;
}): Promise<void> {
  const exchangeKey = `mcp-oauth-exchange:${code}:${state}`;
  const dedupeHit =
    typeof sessionStorage !== 'undefined' &&
    Boolean(sessionStorage.getItem(exchangeKey));
  if (dedupeHit) {
    return;
  }

  const exchangeResult = await exchangeMcpOAuthCallbackOnGateway({
    gatewayUrl,
    code,
    state,
  });

  if (!exchangeResult.success) {
    throw new Error(exchangeResult.error);
  }

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(exchangeKey, '1');
  }
}

export function publishMcpOAuthResult({
  message,
  opener,
  origin,
  broadcastChannel: BroadcastChannelImpl,
}: PublishMcpOAuthResultInput): boolean {
  const hasOpener = Boolean(opener);
  opener?.postMessage(message, origin);
  if (mcpOAuthMessageUsesOpenerPostMessageOnly(message)) {
    return hasOpener;
  }
  const channel = new BroadcastChannelImpl('terra-mcp-oauth');
  channel.postMessage(message);
  channel.close();
  return hasOpener;
}

export function scheduleMcpOAuthCallbackWindowClose({
  delayMs = 500,
}: {
  delayMs?: number;
} = {}): void {
  window.setTimeout(() => {
    window.close();
  }, delayMs);
}
